import type { SelectOption, SettingControl, SettingField } from './registry';

// Builds one field descriptor; every other helper in this file is a named shortcut over it.
const field = (key: string, label: string, control: SettingControl): SettingField => ({ key, label, control });

// Describes a single-line text setting.
export const text = (key: string, label: string): SettingField => field(key, label, 'text');

// Describes a multi-line text setting; paragraphs are split on blank lines at render time.
export const textarea = (key: string, label: string): SettingField => field(key, label, 'textarea');

// Describes a numeric setting bounded by min and max.
export const num = (key: string, label: string, min: number, max: number): SettingField => ({ ...field(key, label, 'number'), min, max });

// Describes an on/off setting.
export const toggle = (key: string, label: string): SettingField => field(key, label, 'toggle');

// Describes a colour setting, always stored as a hex string.
export const color = (key: string, label: string): SettingField => field(key, label, 'color');

// Describes an image setting, always stored as an http(s) URL.
export const image = (key: string, label: string): SettingField => field(key, label, 'image');

// Describes a link setting, stored as an http(s) URL, a site path or an in-page anchor.
export const url = (key: string, label: string): SettingField => field(key, label, 'url');

// Describes a choice of one of the store's navigation menus, stored as the menu's handle.
export const menu = (key: string, label: string): SettingField => field(key, label, 'menu');

// Describes an icon setting, stored as a lucide-react icon name.
export const icon = (key: string, label: string): SettingField => field(key, label, 'icon');

// Describes a formatted-copy setting, edited in the rich-text editor and stored as HTML.
export const richtext = (key: string, label: string): SettingField => field(key, label, 'richtext');

// Describes an instant setting, stored as an ISO-8601 string.
export const datetime = (key: string, label: string): SettingField => field(key, label, 'datetime');

// Describes a size setting, stored as a styles token name or an explicit CSS length.
export const length = (key: string, label: string): SettingField => field(key, label, 'length');

// Describes an optional heading size; omit it and the theme's own type scale stands.
export const headingSizeField = (): SettingField => length('headingFontSize', 'Heading size');

// Describes a radius setting the builder edits as one value or as four separate corners.
export const corners = (key: string, label: string): SettingField =>
  ({ ...field(key, label, 'corners'), help: 'One radius for every corner, or four: start-start, start-end, end-end, end-start.' });

// Describes a start/center/end alignment setting.
export const align = (key: string, label: string): SettingField => field(key, label, 'align');

/** One choice: the value a page stores, and the words the inspector puts beside it. */
export type Choice = readonly [value: string, label: string];

/**
 * Describes a fixed-choice setting whose options read as words rather than as the values they store.
 *
 * `select` spells its options with the stored value, which is why a merchant setting a heading met
 * `h1 h2 h3`, a list met `lower-alpha`, and an image met `_blank` and `lazy`. Those are CSS and HTML
 * spellings; nothing in the product ever teaches them. Every fixed choice a merchant meets on a basic
 * element goes through here instead.
 */
export const named = (key: string, label: string, choices: readonly Choice[]): SettingField =>
  ({ ...field(key, label, 'select'), options: choices.map(([value, caption]) => ({ value, label: caption })) });

// Describes a fixed-choice setting whose values are already the words to show, such as a font weight.
export const select = (key: string, label: string, values: readonly string[]): SettingField => {
  const options: readonly SelectOption[] = values.map((value) => ({ value, label: value }));
  return { ...field(key, label, 'select'), options };
};

// Describes a live picker over the shop's own collections, stored as the collection's own handle.
export const collection = (key: string, label: string, emptyLabel: string): SettingField =>
  ({ ...field(key, label, 'collection'), emptyLabel });

// Describes a nested group of settings edited together.
export const group = (key: string, label: string, fields: readonly SettingField[]): SettingField => ({ ...field(key, label, 'group'), fields });

// Describes a repeatable list whose every item carries the given fields.
export const list = (key: string, label: string, fields: readonly SettingField[]): SettingField => ({ ...field(key, label, 'list'), fields });

// Describes the border group every block-styled section carries.
export const borderFields = (key: string, label: string): SettingField =>
  group(key, label, [length('width', 'Width'), select('style', 'Style', ['none', 'solid', 'dashed', 'dotted']), color('color', 'Colour'), corners('radius', 'Radius')]);

// Describes the three edges a padding group pads at one breakpoint.
const spacingEdgeFields = (): readonly SettingField[] => [length('top', 'Top'), length('bottom', 'Bottom'), length('inline', 'Sides')];

// Describes the padding group every block-styled section carries, desktop first and the phone as its own values.
export const spacingFields = (key: string, label: string): SettingField =>
  group(key, label, [...spacingEdgeFields(), group('mobile', 'On a phone', spacingEdgeFields())]);

// Describes the offset shadow a block casts, independent of the palette the theme derives its own from.
export const shadowFields = (key: string, label: string): SettingField =>
  group(key, label, [
    length('x', 'Across'), length('y', 'Down'), length('blur', 'Blur'), length('spread', 'Spread'),
    color('color', 'Colour'), toggle('inset', 'Inside the box')
  ]);

// Describes the block wrapper — visibility, colours, alignment, width, padding, border and shadow.
export const blockFields = (): SettingField =>
  group('block', 'Block', [
    toggle('show', 'Visible'), color('background', 'Background'), color('textColor', 'Text colour'),
    align('align', 'Alignment'), length('maxWidth', 'Max width'), spacingFields('padding', 'Padding'),
    borderFields('border', 'Border'), shadowFields('shadow', 'Shadow')
  ]);

// Describes a call-to-action button.
export const ctaFields = (key: string, label: string): SettingField =>
  group(key, label, [
    toggle('show', 'Visible'), text('label', 'Label'), url('href', 'Link'),
    select('style', 'Style', ['solid', 'outline', 'text']), select('variant', 'Variant', ['primary', 'secondary']),
    color('background', 'Background'), color('textColor', 'Text colour'), length('radius', 'Radius')
  ]);

// Describes an image slot with its alt text, aspect ratio and radius.
export const mediaFields = (key: string, label: string): SettingField =>
  group(key, label, [
    toggle('show', 'Visible'), image('url', 'Image'), text('alt', 'Alt text'),
    // Free text, not a closed list: a reproduced layout needs the reference's exact box (e.g. 1140/553).
    text('ratio', 'Aspect ratio — auto, or width/height like 16/9'), length('radius', 'Radius')
  ]);

// Describes a live countdown: its deadline, its label, its digits and the captions under them.
export const countdownFields = (key: string, label: string): SettingField =>
  group(key, label, [
    toggle('show', 'Visible'), datetime('endsAt', 'Ends at'), text('label', 'Label'), toggle('showDays', 'Show days'),
    select('style', 'Digit style', ['boxes', 'circles', 'inline']), color('digitBackground', 'Digit background'), color('digitColor', 'Digit colour'),
    group('unitLabels', 'Unit captions', [text('days', 'Days'), text('hours', 'Hours'), text('minutes', 'Minutes'), text('seconds', 'Seconds')])
  ]);

// Describes one form input: whether it shows, its label, its placeholder and whether it is required.
export const formFieldFields = (key: string, label: string): SettingField =>
  group(key, label, [toggle('show', 'Visible'), text('label', 'Label'), text('placeholder', 'Placeholder'), toggle('required', 'Required')]);
